import { useCallback, useMemo, useReducer, useRef } from "react";
import { COPILOT_DOMAIN } from "@launchos/shared-utils/env";
import { getSessionToken } from "@/api/auth";
import { applyCopilotEvent, emptyCopilotState, panelMessages, parseSse, type CopilotState } from "./claudeStream";

// CoPilot, Claude side (CP-3): the same handles LangGraph's useStream gives LangGraphBot —
// messages, values.messages, isLoading, submit, stop, history, joinStream — so the chat
// components don't change (Kyle, Oct 27). The reply streams from the connector's /copilot/turn.

export interface ScreenItem { kind: string; id: string; title: string }

export const approveCopilot = async (approvalId: string, approve: boolean) => {
  const res = await fetch(`${COPILOT_DOMAIN}/copilot/approve`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${getSessionToken() || ""}` },
    body: JSON.stringify({ approvalId, approve }),
  });
  return res.ok;
};

export const copilotStatus = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${COPILOT_DOMAIN}/copilot/status`, { headers: { authorization: `Bearer ${getSessionToken() || ""}` } });
    if (!res.ok) return false;
    return Boolean((await res.json()).enabled);
  } catch {
    return false;
  }
};

let humanCount = 0;
export const STALL_MS = 45000;

export const useClaudeStream = ({
  threadId,
  onThreadId = () => {},
  onFinish = () => {},
  onError = () => {},
  getScreen = () => [],
}: {
  threadId?: string | null;
  onThreadId?: (id: string) => void;
  onFinish?: (thread: any) => void;
  onError?: (e: any) => void;
  getScreen?: () => ScreenItem[];
}) => {
  const [state, dispatch] = useReducer(applyCopilotEvent, undefined, emptyCopilotState);
  const stateRef = useRef<CopilotState>(state);
  stateRef.current = state;
  const abortRef = useRef<AbortController | null>(null);
  const stallRef = useRef<() => void>(() => {});

  const submit = useCallback(async (values: any) => {
    const sent = (values && values.messages) || [];
    const last = [...sent].reverse().find((m: any) => m && m.type === "human");
    const prompt = String((last && last.content) || "").trim();
    if (!prompt) return;
    humanCount += 1;
    // keep a local copy alongside React's, so onFinish sees every event even before a re-render
    let local = stateRef.current;
    const apply = (ev: any) => {
      local = applyCopilotEvent(local, ev);
      dispatch(ev);
    };
    apply({ event: "human", message: { type: "human", id: `human-${Date.now()}-${humanCount}`, content: prompt } });
    const controller = new AbortController();
    abortRef.current = controller;
    let sessionId = stateRef.current.sessionId || threadId || null;
    let silent = false; // set when the watchdog gives up on a silent connection
    try {
      const res = await fetch(`${COPILOT_DOMAIN}/copilot/turn`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${getSessionToken() || ""}` },
        body: JSON.stringify({ prompt, sessionId: sessionId || undefined, screen: getScreen() }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const why = res.status === 401 ? "Please sign in again." : res.status === 403 ? "The CoPilot isn't turned on for this account yet." : res.status === 503 ? "The CoPilot is switched off right now." : `The CoPilot didn't answer (${res.status}).`;
        apply({ event: "done", error: why });
        onError(new Error(why));
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // The server sends a "still working" line every 15 s; 45 s of nothing means the connection
      // died (e.g. a server restart mid-answer) — stop spinning and say so.
      let watchdog = setTimeout(() => ((silent = true), controller.abort()), STALL_MS);
      const heard = () => {
        clearTimeout(watchdog);
        watchdog = setTimeout(() => ((silent = true), controller.abort()), STALL_MS);
      };
      stallRef.current = () => clearTimeout(watchdog);
      for (;;) {
        const { value, done } = await reader.read();
        heard();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSse(buffer);
        buffer = rest;
        for (const ev of events) {
          if (ev.event === "session" && ev.sessionId) {
            sessionId = ev.sessionId;
            onThreadId(ev.sessionId);
          }
          apply(ev);
        }
      }
      apply({ event: "done", sessionId });
    } catch (e: any) {
      if (e && e.name === "AbortError" && silent) apply({ event: "done", sessionId, error: "The CoPilot stopped answering. Try again." });
      else if (e && e.name === "AbortError") apply({ event: "done", sessionId });
      else {
        apply({ event: "done", sessionId, error: "Lost the connection to the CoPilot. Try again." });
        onError(e);
      }
    } finally {
      stallRef.current();
      abortRef.current = null;
      const messages = panelMessages(local);
      onFinish({ values: { messages }, metadata: { thread_id: sessionId } });
    }
  }, [threadId, onThreadId, onFinish, onError, getScreen]);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const messages = useMemo(() => panelMessages(state), [state]);
  return {
    messages,
    values: { messages },
    isLoading: state.isLoading,
    submit,
    stop,
    history: [] as any[],
    joinStream: () => {},
    sessionId: state.sessionId,
  };
};
